import { useState } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import AuthLayout from "./com/AuthLayout";
import { AuthField, PasswordField } from "./com/AuthField";
import { primaryBtn, linkCls, focusRing } from "./com/authStyles";

const api = axios.create({ baseURL: process.env.REACT_APP_API });

// 0300 1234567 / +92 300 1234567 -> 923001234567 (ready for WhatsApp later)
const toIntl = (v) => {
  const d = v.replace(/[\s-]/g, "").replace(/^\+/, "");
  return d.startsWith("0") ? `92${d.slice(1)}` : d;
};

const validateStep = (step, v) => {
  const e = {};
  if (step === 1) {
    if (!v.fullName.trim()) e.fullName = "Enter your full name";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.email.trim()))
      e.email = "Enter a valid email address";
    if (v.password.length < 8) e.password = "Use at least 8 characters";
  } else {
    if (!v.workspaceName.trim())
      e.workspaceName = "Enter your business or store name";
    // Pakistan mobile numbers; change the pattern if you open signup to other countries
    if (!/^(\+92|92|0)3\d{9}$/.test(v.mobile.replace(/[\s-]/g, "")))
      e.mobile = "Enter a valid mobile number, like 0300 1234567";
    if (!/^[a-z0-9._]{3,30}$/i.test(v.username.trim()))
      e.username = "Use 3 to 30 letters, numbers, dots or underscores";
  }
  return e;
};

const SignupPage = ({ clientId }) => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [values, setValues] = useState({
    fullName: "",
    email: "",
    password: "",
    workspaceName: "",
    mobile: "",
    username: "",
  });
  const [formErrors, setFormErrors] = useState({});
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setValues((v) => ({ ...v, [name]: value }));
    if (formErrors[name]) setFormErrors((f) => ({ ...f, [name]: undefined }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;

    const errs = validateStep(step, values);
    setFormErrors(errs);
    if (Object.keys(errs).length) return;

    if (step === 1) {
      setError(null);
      setStep(2);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const email = values.email.trim().toLowerCase();
      const username = values.username.trim();
      const res = await api.post("auths/register", {
        clientId,
        fullName: values.fullName.trim(),
        workspaceName: values.workspaceName.trim(),
        mobile: toIntl(values.mobile),
        email,
        username,
        password: values.password,
      });
      navigate("/verify-otp", {
        state: { username, email, emailSent: res.data?.emailSent !== false },
      });
    } catch (err) {
      if (!err.response) {
        setError(
          "Cannot reach the server. Check your connection and try again.",
        );
      } else {
        const data = err.response.data;
        setError(
          typeof data === "string"
            ? data
            : data?.error || "Registration failed. Try again.",
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const field = (id) => ({
    id,
    value: values[id],
    onChange: handleChange,
    error: formErrors[id],
  });

  return (
    <AuthLayout
      footerLeft={
        <>
          Have an account?{" "}
          <Link to="/login" className={linkCls}>
            Sign in
          </Link>
        </>
      }
    >
      <form onSubmit={handleSubmit} noValidate>
        <div
          className="mb-6 flex items-center gap-3"
          role="img"
          aria-label={`Step ${step} of 2`}
        >
          <span className="flex flex-1 gap-2">
            <span className="h-1.5 flex-1 rounded-full bg-ink" />
            <span
              className={`h-1.5 flex-1 rounded-full ${step === 2 ? "bg-ink" : "bg-ink/15"}`}
            />
          </span>
          <span className="text-[0.85rem] text-muted">Step {step} of 2</span>
        </div>

        <h1 className="m-0 mb-2 font-display text-[2rem] font-bold leading-[1.05] tracking-[-0.02em] min-[900px]:text-[2.4rem]">
          {step === 1 ? "Create your account" : "Set up your workspace"}
        </h1>
        <p className="m-0 mb-6 text-muted">
          {step === 1
            ? "Start with your name, email and password."
            : "Tell us about your business so we can set up your store."}
        </p>

        <div className="grid gap-4">
          {step === 1 ? (
            <>
              <AuthField
                {...field("fullName")}
                label="Full name"
                autoComplete="name"
                autoFocus
              />
              <AuthField
                {...field("email")}
                label="Email"
                type="email"
                autoComplete="email"
                hint="We send your verification code here"
              />
              <PasswordField
                {...field("password")}
                autoComplete="new-password"
                hint="At least 8 characters"
              />
            </>
          ) : (
            <>
              <AuthField
                {...field("workspaceName")}
                label="Business or store name"
                autoComplete="organization"
                autoFocus
              />
              <AuthField
                {...field("mobile")}
                label="Mobile number"
                type="tel"
                autoComplete="tel"
                hint="Example: 0300 1234567"
              />
              <AuthField
                {...field("username")}
                label="Username"
                autoComplete="username"
                hint="Letters, numbers, dots and underscores"
              />
            </>
          )}
        </div>

        {error && (
          <p
            role="alert"
            className="m-0 mt-5 rounded-2xl bg-[#fdecea] px-4 py-3 text-[0.92rem] text-[#8f2418]"
          >
            {error}
          </p>
        )}

        <button
          type="submit"
          disabled={loading}
          className={`mt-6 ${primaryBtn}`}
        >
          {step === 1
            ? "Continue"
            : loading
              ? "Creating your account…"
              : "Create account"}
        </button>

        {step === 2 && (
          <button
            type="button"
            onClick={() => {
              setStep(1);
              setError(null);
            }}
            className={`mt-3 h-11 w-full cursor-pointer rounded-full border border-ink/30 bg-transparent text-[0.95rem] font-medium text-ink hover:bg-white/50 ${focusRing}`}
          >
            Back
          </button>
        )}
      </form>
    </AuthLayout>
  );
};

export default SignupPage;
